<template>
  <div>
    <LoaderReport v-if="loading" />
    <layout-report-with-banner :isProgressDone="true" :showFooter="false" :showLoginButton="true" v-else>
      <template v-slot:banner>
        <v-img :src="headerImage" height="550">
          <v-overlay value="true" absolute>
            <v-container class="mx-auto">
              <v-row class="align-left justify-left">
                <v-col class="ml-4">
                  <h2 v-html="title" class="text-md-h2 text-sm-h3 text-h3 font-weight-medium mt-sm-8 mt-md-0" />
                  <h2
                    v-if="subtitle"
                    v-html="subtitle"
                    class="text-md-h4 text-sm-h5 text-h5 font-weight-medium mt-md-3"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-8 align-left justify-left">
                <v-col xl="4" lg="5" md="6" xs="10" sm="8" cols="10">
                  <v-btn
                    class="ml-4"
                    :color="color"
                    id="start"
                    elevation="0"
                    block
                    dark
                    x-large
                    @click="start"
                    data-test-thg-start-1
                  >
                    {{ submitButtonText }}
                  </v-btn>
                </v-col>
              </v-row>
              <div class="text-caption mb-4 mt-4 mx-4">
                <v-icon small>mdi-shield-check</v-icon>{{ $t("report.trustBanner.privacy") }}
                <v-icon small>mdi-lock</v-icon>{{ $t("report.trustBanner.encryption") }}
                <v-icon small>mdi-check-decagram</v-icon>
                {{ $t("report.trustBanner.datacenter") }}
              </div>
            </v-container>
          </v-overlay>
        </v-img>
      </template>
      <template v-slot:content>
        <v-row align="center" justify="center">
          <v-col md="6">
            <v-card flat max-width="800">
              <v-card-text class="text-md-h2 text-sm-h3 text-h3 mt-md-12 mt-4 font-weight-medium">
                <h1 :style="`color:${partnerColorDarkmode}`" data-test-thg-title>
                  {{ $t("report.thgHome.title") }}
                </h1>
              </v-card-text>
            </v-card>

            <v-card flat max-width="600" v-if="customSubtitle.length">
              <v-card-text
                v-for="(text, idx) of customSubtitle"
                :key="idx"
                class="text-md-h6 text-sm-h6 text-body-2 font-weight-regular"
                v-html="text.text"
              />
            </v-card>
            <v-card v-else flat max-width="600">
              <v-card-text class="text-md-h6 text-sm-h6 text-body-2 font-weight-regular" v-html="subTitle" />
              <v-card-text class="text-md-h6 text-sm-h6 text-body-2 font-weight-regular" v-html="subTitle2" />
            </v-card>

            <v-card flat max-width="600">
              <v-card-text v-if="!isWhiteLabelPartner" class="text-caption" v-html="$t('report.thgImpactFactor.note')">
              </v-card-text>
              <v-card-text v-else class="text-caption" v-html="$t('report.thgImpactFactor.noteWhiteLabelPartner')">
              </v-card-text>
              <v-row class="ma-4 mt-8">
                <login-register-button :showRegisterButton="false" htmlClass="mr-2" data-test-thg-start-2 />
                <v-btn :color="color" id="start" elevation="0" dark large @click="start" data-test-report-start>
                  {{ submitButtonText }}
                </v-btn>
              </v-row>
            </v-card>
          </v-col>
          <v-col md="6">
            <v-img class="ma-8" :src="placeFillerImage" :lazy-src="placeFillerImageLazy" />
          </v-col>
        </v-row>
        <known-form-card :headline="knownFromText" v-if="showTrustBanner" class="mt-8" :logos="logos" />
      </template>
    </layout-report-with-banner>
  </div>
</template>

<script lang="ts">
import DescriptionCard from "@/components/thg/DescriptionCard.vue";
import KnownFormCard from "@/components/thg/KnownFormCard.vue";
import ThgPriceOverview from "@/components/thg/ThgPriceOverview.vue";
import ThgProcessDescriptionCard from "@/components/thg/ThgProcessDescriptionCard.vue";
import LoaderReport from "@/components/utility/LoaderReport.vue";
import LayoutReportWithBanner from "@/layouts/LayoutReportWithBanner.vue";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { PartnerTypeEnum } from "@/store/enum/partner/partner.type.enum";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { PartnerActiveOfferModule } from "@/store/modules/partner-active-config.store";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { Component, Vue } from "vue-property-decorator";
import i18n from "../../plugins/I18nPlugin";
import { GtagModule } from "@/store/modules/gtag.store";
import LoginRegisterButton from "@/components/utility/LoginRegisterButton.vue";

@Component({
  components: {
    LayoutReportWithBanner,
    LoaderReport,
    KnownFormCard,
    DescriptionCard,
    ThgProcessDescriptionCard,
    ThgPriceOverview,
    LoginRegisterButton
  }
})
export default class ThgHome extends Vue {
  screenName = ReportScreenEnum.thghome;

  get loading() {
    return ReportModule.loading && PartnerActiveOfferModule.loading;
  }

  get isWhiteLabelPartner(): boolean {
    return PartnerModule?.partner.partnerType === PartnerTypeEnum.THG_WHITE_LABEL_QUOTE_AS_A_SERVICE;
  }

  get showTrustBanner(): boolean {
    return PartnerModule?.partner.partnerType === PartnerTypeEnum.THG_WHITE_LABEL_MINT_FUTURE;
  }

  get headerImage() {
    if (PartnerModule?.partner?.settings?.reportSettings?.headerImageUrl) {
      return PartnerModule?.partner?.settings?.reportSettings?.headerImageUrl;
    } else if (PartnerModule?.partner?.settings?.headerImageUrl) {
      return PartnerModule?.partner?.settings?.headerImageUrl;
    } else {
      return "https://static.mmmint.ai/wirkaufendeinethg/header-image.png";
    }
  }

  get placeFillerImage() {
    if (PartnerModule?.partner?.settings?.reportSettings?.welcomeImageUrl) {
      return PartnerModule?.partner?.settings?.reportSettings?.welcomeImageUrl;
    }

    if (PartnerModule?.partner.partnerType === PartnerTypeEnum.THG_WHITE_LABEL_MINT_FUTURE) {
      return "https://static.mmmint.ai/public-thg/2023-08-17-header-64857b7c0fb0eec7e4e4b315.png";
    }
    return "https://static.mmmint.ai/public-thg/2023-08-17-header-a638bbe6f267f72125739d7c.png";
  }

  get placeFillerImageLazy() {
    if (PartnerModule?.partner?.settings?.reportSettings?.welcomeImageUrl) {
      return PartnerModule?.partner?.settings?.reportSettings?.welcomeImageUrl;
    }

    if (PartnerModule?.partner.partnerType === PartnerTypeEnum.THG_WHITE_LABEL_MINT_FUTURE) {
      return "https://static.mmmint.ai/public-thg/2023-08-17-header-e9ed43c8d2d3e16a61780cc9.png";
    }
    return "https://static.mmmint.ai/public-thg/2023-08-17-header-266e46bae8e17594b0892c43.png";
  }

  get logos() {
    return [
      "https://wirkaufendeinethg.de/images/trustpartner/bvmw.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/teslaausstatter-blue.png",
      "https://wirkaufendeinethg.de/images/trustpartner/wfo.webp",
      "https://wirkaufendeinethg.de/images/trustpartner/provi.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/vgh.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/acv.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/oeffentliche.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/efahrer.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/oeffentliche-oldenburg.svg",
      "https://wirkaufendeinethg.de/images/trustpartner/ean.png"
    ];
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get partnerColorDarkmode() {
    return this.$vuetify.theme.dark ? "white" : this.color;
  }

  get title() {
    return i18n.t("report.thgHome.headline");
  }

  get knownFromText(): string {
    return String(i18n.tc("report.thgHome.knownFrom"));
  }

  /**
   * Returns a subtitle if partner has a form title configured and its not "-"
   */
  get subtitle(): string {
    if (ReportModule.partner?.settings?.formTitle && ReportModule.partner?.settings?.formTitle !== "-") {
      return ReportModule.partner?.settings?.formTitle;
    }

    return "";
  }

  get customSubtitle() {
    return ReportModule?.partner?.settings?.reportSettings?.welcomeText || [];
  }

  get subTitle(): string {
    if (!this.isAuthenticated) {
      return String(i18n.t("report.thgHome.subTitle"));
    } else {
      return String(i18n.tc("report.thgHome.subTitleAlt"));
    }
  }

  get subTitle2(): string {
    if (!this.isAuthenticated) {
      return String(i18n.t("report.thgHome.subTitle2"));
    } else {
      return String(i18n.tc("report.thgHome.subTitle2"));
    }
  }

  get submitButtonText(): string {
    if (!this.isAuthenticated) {
      return String(i18n.tc("report.thgHome.btnText"));
    } else {
      return String(i18n.tc("report.thgHome.btnTextAlt"));
    }
  }

  get isAuthenticated(): boolean {
    return UserModule.isAuthenticated;
  }

  start() {
    if (ReportModule.nextScreen) {
      const next = ReportModule.nextScreen;
      ReportModule.setStep(next);

      GtagModule.dispatchSelectContentEvent();
      this.$router.push({ path: `/${next}` });
    }
  }

  async mounted() {
    ReportModule.setStep(this.screenName);
  }
}
</script>

<style>
.shadow {
  text-shadow: 3px 3px 10px rgba(0, 0, 0, 0.253);
}

.vue-typer .custom.char.typed {
  color: rgba(0, 0, 0, 0.6);
  text-decoration: underline;
}
.vue-typer .custom.char.selected {
  color: rgba(0, 0, 0, 0.6);
  text-decoration: underline;
}
.vue-typer .custom.caret.typing {
  background-color: rgba(0, 0, 0, 0.6);
}

h1 {
  font-size: unset;
}
</style>
